import { Button } from '@insify/design-system/components';
import clsx from 'clsx';
import { useMatch, useNavigate } from 'react-router-dom';

import styles from './TrackedNavLink.module.css';

import type { SetRequired } from 'type-fest';

// TODO: export these from the design system to align with changes
type ButtonType = SetRequired<Omit<React.ComponentPropsWithoutRef<'button'>, 'children'>, 'type'>;
type AnchorType = SetRequired<Omit<React.ComponentPropsWithoutRef<'a'>, 'children'>, 'href'>;

type Props = {
  to?: string;
  kind?: React.ComponentProps<typeof Button>['kind'];
  onClick?: () => void;
  iconSize?: number;
  strokeWidth?: number;
  icon: React.ReactElement;
  trackingId: string;
  layout?: 'wide' | 'narrow';
} & (ButtonType | AnchorType);

/**
 * Trackable navigation link to avoid nesting buttons inside React Router links.
 * Provides consistency with design system while maintaining link and button semantics.
 * @param layout - 'wide' or 'narrow' button layout for bottom nav styling
 * @returns Button with icon and text
 */
const TrackedNavLink: React.FC<Props> = ({ onClick, icon, title, layout = 'wide', kind = 'tertiary', ...props }) => {
  const navigate = useNavigate();
  const isActive = props.to && useMatch(`${props.to}/*`);

  const handleClick = () => {
    if (onClick) onClick();
    if ('to' in props && props.to) navigate(props.to);
  };

  return (
    <Button
      {...props}
      kind={kind}
      leftIcon={icon}
      onClick={handleClick}
      className={clsx(
        props.className,
        styles.button,
        { [styles.selected]: isActive },
        { [styles.narrow]: layout === 'narrow' },
      )}
      title={title}
    >
      {title}
    </Button>
  );
};

export { TrackedNavLink };
